import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { AlertCircle } from 'lucide-react';

/**
 * Mandatory-reason prompt shown whenever a user flips a control to
 * "Not Applicable". The reason is required by the backend (>=5 chars),
 * so we mirror the same minimum here to give immediate feedback.
 *
 * Usage:
 *   <NaReasonDialog
 *     open={dialog.open}
 *     controlName={dialog.controlName}
 *     onCancel={() => setDialog({ open: false })}
 *     onConfirm={async (reason) => { await persistNA(reason); setDialog({ open: false }); }}
 *   />
 */
const NaReasonDialog = ({ open, controlName, onCancel, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Reset whenever the dialog re-opens so each control gets a fresh prompt.
  useEffect(() => {
    if (open) {
      setReason('');
      setSubmitting(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < 5;
  const canSubmit = trimmed.length >= 5 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onConfirm(trimmed);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onCancel?.(); }}>
      <DialogContent className="max-w-md" data-testid="na-reason-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Mark as Not Applicable
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600">
            Please document why
            {controlName ? <> <span className="font-medium text-slate-800">&ldquo;{controlName}&rdquo;</span></> : ' this control'} doesn&apos;t apply to your scope.
            Auditors will see this reason on the control.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          <Label htmlFor="na-reason" className="text-xs uppercase tracking-wide text-slate-500">
            Reason <span className="text-red-500" aria-hidden>*</span>
          </Label>
          <Textarea
            id="na-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. We don't operate on-prem infrastructure — covered by cloud-only stack."
            rows={4}
            maxLength={1000}
            data-testid="na-reason-input"
            autoFocus
          />
          <div className="flex items-center justify-between text-xs">
            <span className={tooShort ? 'text-red-600' : 'text-slate-500'}>
              {tooShort ? 'Reason must be at least 5 characters.' : 'Minimum 5 characters.'}
            </span>
            <span className="text-slate-400 tabular-nums">{trimmed.length}/1000</span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting} data-testid="na-reason-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-slate-700 hover:bg-slate-800 text-white"
            data-testid="na-reason-submit"
          >
            {submitting ? 'Saving…' : 'Mark Not Applicable'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NaReasonDialog;
